import { jwtDecode } from 'jwt-decode';

const ACCESS_TOKEN_KEY = 'jc_admin_access_token';
const REFRESH_TOKEN_KEY = 'jc_admin_refresh_token';
const USER_INFO_KEY = 'jc_admin_user_info';
const DEVICE_ID_KEY = 'jc_admin_device_id';

export type StaffRole = 'OWNER' | 'JAPAN_WORKER' | 'UZBEK_WORKER';

export type UserInfo = {
  id: string;
  role: StaffRole;
  type: 'staff' | 'user';
  username?: string;
  full_name?: string;
};

type JwtPayload = {
  id: string;
  role: StaffRole;
  type: 'staff' | 'user';
  exp?: number;
};

const safeStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const getAccessToken = (): string | null => safeStorage()?.getItem(ACCESS_TOKEN_KEY) ?? null;

export const getRefreshToken = (): string | null =>
  safeStorage()?.getItem(REFRESH_TOKEN_KEY) ?? null;

export const setTokens = (access: string, refresh: string): void => {
  const s = safeStorage();
  if (!s) return;
  s.setItem(ACCESS_TOKEN_KEY, access);
  s.setItem(REFRESH_TOKEN_KEY, refresh);
};

export const clearTokens = (): void => {
  const s = safeStorage();
  if (!s) return;
  s.removeItem(ACCESS_TOKEN_KEY);
  s.removeItem(REFRESH_TOKEN_KEY);
  s.removeItem(USER_INFO_KEY);
};

export const decodeUserFromToken = (token: string): UserInfo | null => {
  try {
    const payload = jwtDecode<JwtPayload>(token);
    return {
      id: payload.id,
      role: payload.role,
      type: payload.type,
    };
  } catch {
    return null;
  }
};

export const setStoredUser = (user: UserInfo): void => {
  safeStorage()?.setItem(USER_INFO_KEY, JSON.stringify(user));
};

export const getStoredUser = (): UserInfo | null => {
  const raw = safeStorage()?.getItem(USER_INFO_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserInfo;
  } catch {
    return null;
  }
};

export const isAuthenticated = (): boolean => Boolean(getAccessToken());

const generateDeviceId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `admin-${Math.random().toString(36).slice(2)}-${Date.now()}`;
};

export const getOrCreateDeviceId = (): string => {
  const s = safeStorage();
  if (!s) return generateDeviceId();
  const existing = s.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  const fresh = generateDeviceId();
  s.setItem(DEVICE_ID_KEY, fresh);
  return fresh;
};
